import { useEffect, useState } from "react";
import { api } from "./api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 240_000;
const cache = new Map();
const inflight = new Map();   // dedup: key -> shared Promise
let pending = new Map();      // batch window: key -> {key, id, thumb, token, resolve}
let flushTimer = null;

const keyOf = (id, thumb, token, size = "") => `${id}|${thumb ? 1 : 0}|${token ? "m" : ""}|${size}`;

async function fetchSigned(ids, thumb, token, size = "") {
  const missing = ids.filter((id) => {
    const c = cache.get(keyOf(id, thumb, token, size));
    return !c || c.exp < Date.now();
  });
  if (!missing.length) return;
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  const { data } = await api.post("/photos/sign", { ids: missing, thumb, ...(size ? { size } : {}) }, config);
  const exp = Date.now() + REFRESH_MS;
  for (const [id, path] of Object.entries(data.urls || {})) {
    cache.set(keyOf(id, thumb, token, size), { url: `${BACKEND_URL}${path}`, exp });
  }
  // Photos the server omitted (missing doc / unauthorized) are marked failed so
  // viewers show an error state instead of pulsing forever.
  for (const id of missing) {
    if (!(data.urls || {})[id]) cache.set(keyOf(id, thumb, token, size), { url: null, exp: Date.now() + 30_000, failed: true });
  }
}

async function flushPending() {
  flushTimer = null;
  const items = [...pending.values()];
  pending = new Map();
  // one POST per (thumb, token) group instead of one POST per photo
  const groups = new Map();
  for (const it of items) {
    const gk = `${it.thumb ? 1 : 0}|${it.token ? "m" : ""}|${it.size || ""}`;
    if (!groups.has(gk)) groups.set(gk, { thumb: it.thumb, token: it.token, size: it.size || "", ids: [] });
    groups.get(gk).ids.push(it.id);
  }
  await Promise.all([...groups.values()].map(async (g) => {
    try { await fetchSigned([...new Set(g.ids)], g.thumb, g.token, g.size); } catch {}
  }));
  for (const it of items) { inflight.delete(it.key); it.resolve(); }
}

function requestSigned(id, thumb, token, size = "") {
  const key = keyOf(id, thumb, token, size);
  const c = cache.get(key);
  if (c && c.exp > Date.now()) return Promise.resolve();
  if (inflight.has(key)) return inflight.get(key);
  const p = new Promise((resolve) => {
    pending.set(key, { key, id, thumb, token, size, resolve });
    if (!flushTimer) flushTimer = setTimeout(flushPending, 30);
  });
  inflight.set(key, p);
  return p;
}

export function useSignedPhotoUrl(photoId, thumb = false, token = null, size = "") {
  const [url, setUrl] = useState(() => (photoId ? cache.get(keyOf(photoId, thumb, token, size))?.url || "" : ""));
  useEffect(() => {
    let alive = true;
    if (!photoId) { setUrl(""); return; }
    const k = keyOf(photoId, thumb, token, size);
    const c = cache.get(k);
    if (c && c.exp > Date.now()) { setUrl(c.url); return; }
    requestSigned(photoId, thumb, token, size)
      .then(() => { if (alive) { const e = cache.get(k); setUrl(e ? e.url : ""); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [photoId, thumb, token, size]);
  return url;
}

export function invalidatePhotoUrl(photoId) {
  for (const k of [...cache.keys()]) {
    if (k.startsWith(`${photoId}|`)) cache.delete(k);
  }
}

export async function getSignedPhotoUrl(photoId, thumb = false, token = null, size = "") {
  const k = keyOf(photoId, thumb, token, size);
  const c = cache.get(k);
  if (c && c.exp > Date.now()) return c.url;
  await requestSigned(photoId, thumb, token, size);
  return cache.get(k)?.url || "";
}

export async function getSignedPdfUrl(projectId) {
  const { data } = await api.get(`/projects/${projectId}/report.pdf/sign`);
  return `${BACKEND_URL}${data.url}`;
}

// ---- Stamped derivatives ----
const stampCache = new Map();
const stampKey = (id, level, size, lang) => `${id}|${level}|${size}|${lang}`;

export function useSignedStampUrl(photoId, enabled, level = "exact", size = "full", lang = "en") {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let alive = true;
    if (!photoId || !enabled) { setUrl(""); return; }
    const k = stampKey(photoId, level, size, lang);
    const c = stampCache.get(k);
    if (c && c.exp > Date.now()) { setUrl(c.url); return; }
    api.post("/photos/sign-stamp", { ids: [photoId], level, size, lang })
      .then(({ data }) => {
        const p = data.urls?.[photoId];
        if (p) {
          const u = `${BACKEND_URL}${p}`;
          stampCache.set(k, { url: u, exp: Date.now() + REFRESH_MS });
          if (alive) setUrl(u);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [photoId, enabled, level, size, lang]);
  return url;
}

export async function getSignedStampUrl(photoId, level = "exact", size = "full", lang = "en", dl = false) {
  const { data } = await api.post("/photos/sign-stamp", { ids: [photoId], level, size, lang });
  const p = data.urls?.[photoId];
  return p ? `${BACKEND_URL}${p}${dl ? "&dl=1" : ""}` : "";
}

export async function getSignedPhotoDownloadUrl(photoId, size = "original") {
  const { data } = await api.post("/photos/sign", { ids: [photoId], size });
  const p = data.urls?.[photoId];
  return p ? `${BACKEND_URL}${p}&dl=1` : "";
}
